import { defineMessages, useIntl } from "react-intl";

import { isFeatureEnabled } from "util/feature_detection";
import { SPLIT_BOOKMARKED_PDF_IN_TRANSACTION_UI } from "constants/feature_gates";
import { isHybridTransactionType } from "common/mortgage/transactions/utils";
import { DocumentRequirementEnum, OrganizationTransactionLabels } from "graphql_globals";

import { type SectionContract, type SectionComponentProps } from "../../form";
import { SectionHeader, downgradeDisplay, setEditableDisplay } from "../../common";
import type { DocumentsTransaction } from "./transaction_fragment.graphql";
import type { DocumentsOrganization } from "./organization_fragment.graphql";
import type { DocumentsUser } from "./user_fragment.graphql";
import { DocumentUploader } from "./document_uploader";
import { modifyDocumentsConfigValue } from "./util";

const MESSAGES = defineMessages({
  documents: {
    id: "f1418716-f624-4d9d-a63e-d6091db46073",
    defaultMessage: "Documents",
  },
});

export const DOCUMENT_SECTION_ID = "transaction-creation-section-documents";

export const CONFIGS = {
  documentUploader: "documentUploader",
  defaultDocPermissions: "defaultDocPermissions",
  defaultDocRequirement: "defaultDocRequirement",
  canSetDocPermissions: "canSetDocPermissions",
  canSetDocRequirements: "canSetDocRequirements",
  canRequireAttestation: "canRequireAttestation",
  splitBookmarkedPdf: "splitBookmarkedPdf",
  supportedFileTypes: "supportedFileTypes",
  canRequireWitness: "canRequireWitness",
  placeAnOrderUIEnabled: "placeAnOrderUIEnabled",
} as const;

export type DocumentSectionProps = SectionComponentProps<
  DocumentsTransaction,
  DocumentsOrganization,
  DocumentsUser
>;

function DocumentsSection({
  transaction,
  organization,
  user,
  config,
  form,
  onSave,
}: DocumentSectionProps) {
  const intl = useIntl();

  return (
    <>
      <SectionHeader iconName="document-pdf" id={DOCUMENT_SECTION_ID}>
        {intl.formatMessage(MESSAGES.documents)}
      </SectionHeader>
      <DocumentUploader
        transaction={transaction}
        organization={organization}
        user={user}
        config={config}
        form={form}
        onSave={onSave}
      />
    </>
  );
}

export const DOCUMENTS_SECTION = {
  Component: DocumentsSection,
  configs: CONFIGS,
  modifyConfig({ transaction, sectionConfig, permissions, organization }) {
    // [BIZ-9616] Confirm correct permissions are included for REAL_ESTATE_PROOF and REAL_ESTATE_ESIGN
    // TODO: revisit with attestation config for canRequireAttestion
    const { placeAnOrderUIEnabled } = sectionConfig;
    const modifiedConfig = { ...sectionConfig };
    const { orderProgress, transactionType } = transaction;

    // only lender will care about these values for determining PAO ui
    // for title they will always be false
    const readyToSendOrder =
      orderProgress?.label === OrganizationTransactionLabels.AWAITING_LENDER_APPROVAL;
    const isHybridTransaction = transactionType && isHybridTransactionType(transactionType);

    // esign/notarization specific
    if (isFeatureEnabled(transaction.organization, SPLIT_BOOKMARKED_PDF_IN_TRANSACTION_UI)) {
      modifyDocumentsConfigValue(modifiedConfig, "splitBookmarkedPdf", true);
    }

    if (
      organization.canRequireVerificationOfFact &&
      sectionConfig.defaultDocRequirement === DocumentRequirementEnum.NOTARIZATION
    ) {
      modifyDocumentsConfigValue(modifiedConfig, "canRequireAttestation", true);
    }

    if (!permissions.hasPermissionFor("editOrganizationTransactions")) {
      modifyDocumentsConfigValue(modifiedConfig, "canSetDocRequirements", false);
      modifyDocumentsConfigValue(modifiedConfig, "canSetDocPermissions", false);
      downgradeDisplay(modifiedConfig, "documentUploader", "readonly");
    } else {
      modifyDocumentsConfigValue(modifiedConfig, "canSetDocRequirements", true);
      modifyDocumentsConfigValue(modifiedConfig, "canSetDocPermissions", true);
    }

    // title/lender specific
    if (placeAnOrderUIEnabled && !readyToSendOrder && !isHybridTransaction) {
      modifyDocumentsConfigValue(modifiedConfig, "canSetDocRequirements", false);
      modifyDocumentsConfigValue(modifiedConfig, "canSetDocPermissions", false);
      downgradeDisplay(modifiedConfig, "documentUploader", "readonly");
    }

    if (permissions.hasPermissionFor("manageOpenOrders")) {
      modifyDocumentsConfigValue(modifiedConfig, "placeAnOrderUIEnabled", false);
      modifyDocumentsConfigValue(modifiedConfig, "canSetDocRequirements", true);
      modifyDocumentsConfigValue(modifiedConfig, "canSetDocPermissions", true);
      setEditableDisplay(modifiedConfig, "documentUploader");
    }

    return modifiedConfig;
  },
} satisfies SectionContract<
  Record<never, never>,
  Record<never, never>,
  DocumentsTransaction,
  DocumentsOrganization,
  DocumentsUser,
  typeof CONFIGS
>;
